import { useState, useEffect, useRef, useCallback } from 'react';

const WS_URL = 'wss://stream.binance.com:9443/ws';

const SYMBOL_PAIRS: Record<string, string> = {
  BTCUSDT: 'BTC-PERP',
  ETHUSDT: 'ETH-PERP',
  SOLUSDT: 'SOL-PERP',
  ARBUSDT: 'ARB-PERP',
  DOGEUSDT: 'DOGE-PERP',
  LINKUSDT: 'LINK-PERP',
  AVAXUSDT: 'AVAX-PERP',
  OPUSDT: 'OP-PERP',
  SUIUSDT: 'SUI-PERP',
  SEIUSDT: 'SEI-PERP',
};

const INITIAL_PRICES: Record<string, number> = {
  'BTC-PERP': 45230.5,
  'ETH-PERP': 3124.8,
  'SOL-PERP': 142.3,
  'ARB-PERP': 0.89,
  'DOGE-PERP': 0.12,
  'LINK-PERP': 14.56,
  'AVAX-PERP': 35.2,
  'OP-PERP': 2.34,
  'SUI-PERP': 1.45,
  'SEI-PERP': 0.56,
};

const STREAMS = Object.keys(SYMBOL_PAIRS).map((s) => `${s.toLowerCase()}@ticker`).join('/');

export interface TradeItem {
  pair: string;
  side: 'long' | 'short';
  price: number;
  size: number;
  time: string;
  id: number;
}

export function useBinancePrices() {
  const [prices, setPrices] = useState<Record<string, number>>(INITIAL_PRICES);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const pricesRef = useRef<Record<string, number>>(INITIAL_PRICES);

  const updatePrices = useCallback((newPrices: Record<string, number>) => {
    pricesRef.current = { ...pricesRef.current, ...newPrices };
    setPrices((prev) => ({ ...prev, ...newPrices }));
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let mounted = true;

    const connect = () => {
      if (!mounted) return;

      try {
        ws = new WebSocket(`${WS_URL}/${STREAMS}`);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mounted) return;
          setConnected(true);
        };

        ws.onmessage = (event) => {
          if (!mounted) return;
          try {
            const data = JSON.parse(event.data);
            const items = Array.isArray(data) ? data : [data];
            const updates: Record<string, number> = {};

            for (const item of items) {
              const sym = (item.s || '').toUpperCase();
              const pair = SYMBOL_PAIRS[sym];
              if (pair && item.c) {
                updates[pair] = parseFloat(item.c);
              }
            }

            if (Object.keys(updates).length > 0) {
              updatePrices(updates);
            }
          } catch {
            // ignore parse errors
          }
        };

        ws.onerror = () => {
          // silently handle error
        };

        ws.onclose = () => {
          if (!mounted) return;
          setConnected(false);
          wsRef.current = null;
          // reconnect after 5s
          reconnectTimer = setTimeout(connect, 5000);
        };
      } catch {
        reconnectTimer = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      mounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
    };
  }, [updatePrices]);

  // Generate a new trade using latest prices
  const generateTrade = useCallback((): TradeItem => {
    const allPairs = Object.values(SYMBOL_PAIRS);
    const pair = allPairs[Math.floor(Math.random() * allPairs.length)];
    const side = Math.random() > 0.4 ? 'long' : 'short';

    const sizePresets: Record<string, number> = {
      'BTC-PERP': 2.5,
      'ETH-PERP': 5.0,
      'SOL-PERP': 10.0,
      'ARB-PERP': 2500,
      'DOGE-PERP': 50000,
      'LINK-PERP': 100,
      'AVAX-PERP': 50,
      'OP-PERP': 500,
      'SUI-PERP': 2000,
      'SEI-PERP': 10000,
    };

    const baseSize = sizePresets[pair] || 100;
    const variance = 0.5 + Math.random(); // 0.5x - 1.5x variance
    const size = parseFloat((baseSize * variance).toFixed(2));

    return {
      pair,
      side,
      price: pricesRef.current[pair] || INITIAL_PRICES[pair] || 0,
      size,
      time: 'just now',
      id: Date.now() + Math.random(),
    };
  }, []);

  return { prices, connected, generateTrade };
}